import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { useState } from "react";

import { Example } from "./types";

import { ExamplesProvider, useExamples } from "@/components/ExamplesSelector";
import ExampleSelectionDialog from "@/components/ExamplesSelector/ExampleSelectionDialog";
import SelectDataset from "@/components/SelectDataset";
import { useUserToken } from "@/context/auth-context";
import { Entity, EntityType, useDatasetGroup } from "@/queries";

interface DatasetRowValue {
  row: number;
  value: object;
}

type DatasetSelectionResult =
  | { id: number; name: string; row: number; value: object }
  | { id: number; name: string; rows: DatasetRowValue[] };

interface DatasetPickerProps {
  onSelect?: (dataset: DatasetSelectionResult) => void;
  singleSelect?: boolean;
  maxSelectable?: number;
}

interface MultiRowSelectorProps {
  selectedDatasetId: number;
  datasetName: string;
  onSelect?: (dataset: DatasetSelectionResult) => void;
  singleSelect?: boolean;
  maxSelectable?: number;
}

const MultiRowSelector = ({
  selectedDatasetId,
  datasetName,
  onSelect,
  singleSelect = true,
  maxSelectable,
}: MultiRowSelectorProps) => {
  const examples = useExamples();

  const handleAddExample = (example: Example) => {
    examples.addExample(example);
  };

  const handleSelect = () => {
    if (onSelect && selectedDatasetId && examples.selectedExamples.length > 0) {
      if (singleSelect) {
        onSelect({
          id: selectedDatasetId,
          name: datasetName,
          row: examples.selectedExamples[0].id,
          value: examples.selectedExamples[0].value,
        });
      } else {
        onSelect({
          id: selectedDatasetId,
          name: datasetName,
          rows: examples.selectedExamples.map(example => ({
            row: example.id,
            value: example.value,
          })),
        });
      }
    }
  };

  return selectedDatasetId ? (
    <ExampleSelectionDialog
      datasetId={selectedDatasetId}
      onAddExample={handleAddExample}
      onSelect={handleSelect}
      singleSelect={singleSelect}
      selectedExamples={examples.selectedExamples}
      maxSelectable={maxSelectable}
      defaultOpen
      hideTrigger
    />
  ) : null;
};

const DatasetPicker = ({ onSelect, singleSelect = true, maxSelectable }: DatasetPickerProps) => {
  const [selectedDataset, setSelectedDataset] = useState<Entity<EntityType.DATASET> | undefined>();

  const selectedDatasetId = selectedDataset?.id;

  const handleDatasetSelect = (dataset: Entity<EntityType.DATASET>) => {
    setSelectedDataset(dataset);
  };

  const userToken = useUserToken();

  // Only fetch when we have a valid dataset ID
  const groups = useDatasetGroup(userToken, selectedDatasetId ? String(selectedDatasetId) : "");
  const isLoadingGroups = groups?.isLoading ?? false;

  const datasets = selectedDatasetId
    ? (groups?.data?.dataset_group.datasets || []).filter(d => d.version_number > 0)
    : [];

  const selectedVersionId = datasets.find(
    d =>
      d.version_number ===
      (selectedDataset?.version_number ?? selectedDataset?.metadata?.latest_version_number),
  )?.id;

  // Only show draft warning after query has loaded and confirmed no versions
  const isDraftDataset = selectedDataset && !isLoadingGroups && datasets.length === 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center gap-2">
        <SelectDataset
          dataset={selectedDataset}
          className="w-full !p-1"
          onSelect={handleDatasetSelect}
        />
        {datasets.length > 0 && selectedVersionId && (
          <ExamplesProvider key={selectedVersionId}>
            <MultiRowSelector
              selectedDatasetId={selectedVersionId}
              datasetName={selectedDataset?.name || "Dataset"}
              onSelect={onSelect}
              singleSelect={singleSelect}
              maxSelectable={maxSelectable}
            />
          </ExamplesProvider>
        )}
      </div>
      {isDraftDataset && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50
            p-3 text-sm"
        >
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="flex flex-col gap-1">
            <span className="font-medium text-amber-900">Draft dataset selected</span>
            <span className="text-amber-700">
              This dataset has no published versions. Please publish a version before selecting
              examples.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatasetPicker;
